import React, { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Shield, Check, X } from 'lucide-react'
import axios from 'axios'
import MDTLogo from 'images/MDT_FullLogo.png'

interface ConsentData {
	clientName: string
	scopes: string[]
	userEmail: string
	userName: string
	clientId: string
	redirectUri: string
	state: string
	scope: string
	codeChallenge: string
	codeChallengeMethod: string
}

const SCOPE_DESCRIPTIONS: Record<string, string> = {
	profile: 'View your profile information',
	email: 'View your email address',
}

const OAuthConsent = () => {
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const [consentData, setConsentData] = useState<ConsentData | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [submitting, setSubmitting] = useState(false)

	const userLogin = useSelector((state: any) => state.userLogin)
	const { userInfo } = userLogin

	const clientId = searchParams.get('client_id') || ''
	const redirectUri = searchParams.get('redirect_uri') || ''
	const responseType = searchParams.get('response_type') || ''
	const state = searchParams.get('state') || ''
	const scope = searchParams.get('scope') || ''
	const codeChallenge = searchParams.get('code_challenge') || ''
	const codeChallengeMethod = searchParams.get('code_challenge_method') || ''

	useEffect(() => {
		if (!userInfo) {
			// Redirect to login with return_to so user comes back to the consent
			// page (with all OAuth + PKCE params intact) after authenticating.
			const returnTo = `/oauth/consent?${searchParams.toString()}`
			navigate(`/login?redirect=${encodeURIComponent(returnTo)}`, { replace: true })
			return
		}

		// Fetch consent data from backend
		axios
			.get('/oauth/authorize', {
				params: {
					client_id: clientId,
					redirect_uri: redirectUri,
					response_type: responseType,
					state,
					scope,
					code_challenge: codeChallenge,
					code_challenge_method: codeChallengeMethod,
				},
				headers: { Authorization: `Bearer ${userInfo.token}` },
			})
			.then(res => {
				setConsentData(res.data)
			})
			.catch(err => {
				const msg = err.response?.data?.message || 'Failed to load authorization request.'
				setError(msg)
			})
	}, [
		userInfo,
		clientId,
		redirectUri,
		responseType,
		state,
		scope,
		codeChallenge,
		codeChallengeMethod,
		navigate,
		searchParams,
	])

	const handleDecision = async (approved: boolean) => {
		if (!consentData || !userInfo) return
		setSubmitting(true)

		try {
			const res = await axios.post(
				'/oauth/authorize/decision',
				{
					approved,
					clientId: consentData.clientId,
					redirectUri: consentData.redirectUri,
					state: consentData.state,
					scope: consentData.scope,
					codeChallenge: consentData.codeChallenge,
					codeChallengeMethod: consentData.codeChallengeMethod,
				},
				{ headers: { Authorization: `Bearer ${userInfo.token}` } },
			)
			// Redirect to the client app
			window.location.href = res.data.redirectTo
		} catch (err: any) {
			setError(err.response?.data?.message || 'Authorization failed.')
			setSubmitting(false)
		}
	}

	if (error) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
				<div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
					<img src={MDTLogo} alt="MYDealTeams" className="h-8 mx-auto mb-6" />
					<div className="text-red-500 mb-4">
						<Shield className="w-12 h-12 mx-auto mb-2" />
					</div>
					<h2 className="text-xl font-semibold text-gray-900 mb-2">Authorization Error</h2>
					<p className="text-gray-600 mb-6">{error}</p>
					<button
						onClick={() => navigate('/')}
						className="px-6 py-2 rounded-xl text-white bg-[#0080fe] hover:bg-[#0073e6] transition-colors"
					>
						Return to MYDealTeams
					</button>
				</div>
			</div>
		)
	}

	if (!consentData) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<div className="text-gray-500">Loading authorization request...</div>
			</div>
		)
	}

	return (
		<div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
			<div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full">
				{/* Logo */}
				<div className="text-center mb-6">
					<img src={MDTLogo} alt="MYDealTeams" className="h-8 mx-auto mb-4" />
				</div>

				{/* Request description */}
				<div className="text-center mb-6">
					<h2 className="text-xl font-semibold text-gray-900 mb-2">
						<span className="text-[#0080fe]">{consentData.clientName}</span> is requesting access to your account
					</h2>
				</div>

				{/* Scopes */}
				<div className="bg-gray-50 rounded-xl p-4 mb-6">
					<p className="text-sm font-medium text-gray-700 mb-3">This will allow {consentData.clientName} to:</p>
					<ul className="space-y-2">
						{consentData.scopes.map(s => (
							<li key={s} className="flex items-center gap-2 text-sm text-gray-700">
								<Check className="w-4 h-4 text-green-500 flex-shrink-0" />
								{SCOPE_DESCRIPTIONS[s] || s}
							</li>
						))}
					</ul>
				</div>

				{/* Signed in as */}
				<div className="text-center text-sm text-gray-500 mb-6">
					Signed in as <span className="font-medium text-gray-700">{consentData.userEmail}</span>
				</div>

				{/* Buttons */}
				<div className="flex gap-3">
					<button
						onClick={() => handleDecision(false)}
						disabled={submitting}
						className="flex-1 px-4 py-2.5 rounded-xl border border-gray-300 bg-white text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
					>
						Deny
					</button>
					<button
						onClick={() => handleDecision(true)}
						disabled={submitting}
						className="flex-1 px-4 py-2.5 rounded-xl bg-[#0080fe] text-white font-medium hover:bg-[#0073e6] transition-colors disabled:opacity-50"
					>
						{submitting ? 'Authorizing...' : 'Authorize'}
					</button>
				</div>
			</div>
		</div>
	)
}

export default OAuthConsent
